import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, loginUrl, type CollectionStatus } from '../api';

/** A status label that never relies on colour alone to carry its meaning. */
export function StatusChip({ status }: { status: CollectionStatus }) {
  const map: Record<CollectionStatus, { cls: string; label: string }> = {
    success: { cls: 'chip-ok', label: 'vollständig' },
    empty: { cls: 'chip-neutral', label: 'keine Daten' },
    partial: { cls: 'chip-warn', label: 'unvollständig' },
    failed: { cls: 'chip-danger', label: 'Abruf fehlgeschlagen' },
  };
  const s = map[status] ?? map.success;
  return <span className={`chip ${s.cls}`}>{s.label}</span>;
}

export function Chip({ kind = 'neutral', children }: { kind?: string; children: ReactNode }) {
  return <span className={`chip chip-${kind}`}>{children}</span>;
}

export function Notice({
  kind = 'info',
  title,
  children,
}: {
  kind?: 'info' | 'warn' | 'danger';
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`notice notice-${kind}`} role={kind === 'danger' ? 'alert' : undefined}>
      {title && <strong>{title}</strong>}
      {children}
    </div>
  );
}

export function PageIntro({ eyebrow, title, children }: { eyebrow: string; title: string; children?: ReactNode }) {
  return (
    <header className="page-intro">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      {children && <p className="page-lead">{children}</p>}
    </header>
  );
}

export function Loading({ label = 'Lädt…' }: { label?: string }) {
  return (
    <div className="state state-loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="state state-empty">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

/**
 * A failed request is an error, never an empty day. An expired session says so
 * and offers the same login the shell uses, returning to the current page.
 */
export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  if (error instanceof ApiError && error.isUnauthenticated) {
    const redirect = window.location.pathname + window.location.search;
    return (
      <div className="state state-error" role="alert">
        <h2>Sitzung abgelaufen</h2>
        <p>Bitte melde dich neu an. Du kommst danach hierher zurück.</p>
        <a className="button primary" href={loginUrl(redirect)}>
          Neu anmelden
        </a>
      </div>
    );
  }
  return (
    <div className="state state-error" role="alert">
      <h2>Konnte nicht geladen werden</h2>
      <p>{error.message}</p>
      <p className="muted small">Das ist ein Fehler beim Abruf – nicht ein leerer Tag.</p>
      {onRetry && (
        <button className="button" type="button" onClick={onRetry}>
          Erneut versuchen
        </button>
      )}
    </div>
  );
}

/** Small hook so every page handles loading, errors and re-fetching the same way. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fn()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
